import { calculateSimulatorScenario } from './utils/calculations';

const COUNTRY_DEFAULTS = {
	ARGENTINA: { iva: 0.21, ivaaplicado: 0.21, iibb: 0.05, ica: 0,     dolar: 1200, basefreeshipping: 33000, basefixrate: 1255, basefixrate2: 2500, basefixrate3: 3030 },
	MEXICO:    { iva: 0.16, ivaaplicado: 0.16, iibb: 0,    ica: 0,     dolar: 17,   basefreeshipping: 299,   basefixrate: 25,   basefixrate2: 30,   basefixrate3: 37   },
	COLOMBIA:  { iva: 0.19, ivaaplicado: 0.19, iibb: 0,    ica: 0.004, dolar: 4000, basefreeshipping: 60000, basefixrate: 2500, basefixrate2: 4000, basefixrate3: 0    },
};

const buildDefaultScenario = (id, pais = 'ARGENTINA') => {
	const d = COUNTRY_DEFAULTS[pais] || COUNTRY_DEFAULTS.ARGENTINA;
	return {
		id,
		label: `Escenario ${id}`,
		productInfo: null,
		pais,
		site: 'MELI',
		categoria: null,
		cuotasMode: 'clasica',
		cuotasCant: 3,
		usdcost: 0,
		localcost: 0,
		dolar: 0,
		pvpdisbyte: 0,
		iva: d.iva,
		ivaaplicado: d.ivaaplicado,
		shippingmode: 'me2',
		freeshipping: false,
		basefreeshipping: d.basefreeshipping ?? 0,
		freeshippingcostnoivac: 0,
		freeshippingcostr: 0,
		melifeeadjperc: 0.16,
		iibb: d.iibb,
		ica: d.ica,
		rebateperc: 0,
		pvpdiscount: 0,
		pvpbuyer: 0,
		basefixrate: d.basefixrate ?? 0,
		basefixrate2: d.basefixrate2 ?? 0,
		basefixrate3: d.basefixrate3 ?? 0,
		pvp: 0,
		results: null,
	};
};

export const launchSimulatorStoreSlice = (set, get) => ({
	lsScenarios: [
		buildDefaultScenario('A'),
		buildDefaultScenario('B'),
		buildDefaultScenario('C'),
	],

	lsUpdateScenarioField: (id, field, value) => {
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) => {
				if (s.id !== id) return s;
				// Si el usuario toca usdcost o dolar manualmente, el localcost fijo ya no aplica
				const extra = (field === 'usdcost' || field === 'dolar') ? { localcost: 0 } : {};
				const updated = { ...s, [field]: value, ...extra };
				if (field === 'pvpdisbyte' || field === 'pvp') {
					const pvp = field === 'pvp' ? value : s.pvp;
					const pvpdisbyte = field === 'pvpdisbyte' ? value : s.pvpdisbyte;
					updated.pvpdiscount = pvp > 0 && pvpdisbyte > 0 && pvp > pvpdisbyte
						? parseFloat(((pvp - pvpdisbyte) / pvp).toFixed(6))
						: 0;
				}
				return updated;
			}),
		}));
		get().lsRecalculate(id);
	},

	lsChangeSite: (id, site) => {
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) => {
				if (s.id !== id) return s;
				const melifeeadjperc = site === 'VTEX' ? 0.025 : 0.13;
				return { ...s, site, melifeeadjperc };
			}),
		}));
		get().lsRecalculate(id);
	},

	lsSetExchangeRate: (id, tasa) => {
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) =>
				s.id === id ? { ...s, dolar: tasa } : s
			),
		}));
		get().lsRecalculate(id);
	},

	lsChangeCategoria: (id, categoria, fee) => {
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) =>
				s.id === id ? { ...s, categoria, melifeeadjperc: fee } : s
			),
		}));
		get().lsRecalculate(id);
	},

	lsChangeCountry: (id, pais) => {
		const d = COUNTRY_DEFAULTS[pais] || COUNTRY_DEFAULTS.ARGENTINA;
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) =>
				s.id === id
					? { ...s, pais, site: 'MELI', iva: d.iva, ivaaplicado: d.ivaaplicado, iibb: d.iibb, ica: d.ica, basefreeshipping: d.basefreeshipping ?? 0, basefixrate: d.basefixrate ?? 0, basefixrate2: d.basefixrate2 ?? 0, basefixrate3: d.basefixrate3 ?? 0 }
					: s
			),
		}));
		get().lsRecalculate(id);
	},

	lsLoadProduct: (id, row, pais) => {
		const iva = row.iva || COUNTRY_DEFAULTS[pais]?.iva || 0;
		const pvpdisbyte = row.pvpdisbyte || 0;
		// Back-calculate the real basefixrate from the API's pre-computed fixratenoiva.
		// The API can return an inconsistent basefixrate (e.g. 1115) while its own
		// fixratenoiva was computed with a different value (e.g. 1250 → 1033.06 s/IVA).
		// We correct the tier that matches the current pvpdisbyte so recalculations
		// stay consistent with Pricing's pre-computed results.
		// Back-calculate melifeeadjperc from the API's pre-computed melifeeadjnoiva.
		// row.melifeeadjperc can differ from what the backend actually used (e.g. 0.15
		// stored in the item vs 0.16 used internally → melifeeadjnoiva = 1018.88).
		// Reverse: melifeeadjperc = melifeeadjnoiva * (1 + iva) / pvpdisbyte.
		const melifeeadjperc = (row.melifeeadjnoiva > 0 && pvpdisbyte > 0)
			? parseFloat((row.melifeeadjnoiva * (1 + iva) / pvpdisbyte).toFixed(6))
			: (row.melifeeadjperc || 0.13);

		let basefixrate = row.basefixrate || 0;
		let basefixrate2 = row.basefixrate2 || 0;
		let basefixrate3 = row.basefixrate3 || 0;
		if (row.fixratenoiva > 0) {
			const corrected = parseFloat((row.fixratenoiva * (1 + iva)).toFixed(2));
			if (pais === 'ARGENTINA') {
				if (pvpdisbyte <= 15000) basefixrate = corrected;
				else if (pvpdisbyte <= 25000) basefixrate2 = corrected;
				else if (pvpdisbyte <= 33000) basefixrate3 = corrected;
			} else if (pais === 'COLOMBIA') {
				if (pvpdisbyte < 30000) basefixrate = corrected;
				else basefixrate2 = corrected;
			} else if (pais === 'MEXICO') {
				if (pvpdisbyte < 99) basefixrate = corrected;
				else if (pvpdisbyte <= 149) basefixrate2 = corrected;
				else if (pvpdisbyte <= 299) basefixrate3 = corrected;
			} else {
				basefixrate = corrected;
			}
		}

		const updated = {
			...buildDefaultScenario(id, pais),
			categoria: null,
			cuotasMode: 'clasica',
			cuotasCant: 3,
			productInfo: {
				sku: row.sku,
				title: row.title,
				thumbnail: row.thumbnail,
				site: row.site,
				status: row.status,
				listingtypename: row.listingtypename || null,
				pvp_actual: row.pvp,
				pvpdisbyte_actual: row.pvpdisbyte,
				margen_actual: parseFloat((row.margen * 100).toFixed(2)),
				roi_actual: parseFloat((row.roi * 100).toFixed(2)),
			},
			pais,
			site: row.site || 'MELI',
			usdcost: row.usdcost || 0,
			localcost: row.localcost || 0,
			dolar: (row.usdcost > 0 && row.localcost > 0)
				? parseFloat((row.localcost / row.usdcost).toFixed(4))
				: (row.dolar || COUNTRY_DEFAULTS[pais]?.dolar || 1),
			pvpdisbyte,
			iva,
			ivaaplicado: row.ivaaplicado || row.iva || COUNTRY_DEFAULTS[pais]?.ivaaplicado || 0,
			shippingmode: row.shippingmode || 'me2',
			freeshipping: row.freeshipping || false,
			basefreeshipping: row.basefreeshipping || 0,
			freeshippingcostnoivac: row.freeshippingcostnoivac || 0,
			freeshippingcostr: row.freeshippingcostr || 0,
			melifeeadjperc,
			iibb: row.iibb || 0,
			ica: row.ica || 0,
			rebateperc: row.rebateperc || 0,
			pvpdiscount: row.pvpdiscount || 0,
			pvpbuyer: row.pvpbuyer || 0,
			basefixrate,
			basefixrate2,
			basefixrate3,
			pvp: row.pvp || 0,
		};
		// Usar valores pre-calculados del API para el resultado inicial.
		// Para MEX/COL sin free shipping el API retorna el costo operativo en
		// freeshippingcostnoivar (no en fixratenoiva). Lo movemos a fixRate para
		// que el desglose muestre "Costo operativo" igual que tras el recálculo.
		const isOpCostRow = (pais === 'MEXICO' || pais === 'COLOMBIA') && !row.freeshipping && (row.freeshippingcostnoivar > 0);
		const initialResults = (row.pvpdisbyte > 0 && row.profit != null) ? {
			localcost: row.localcost || 0,
			margen: parseFloat((row.margen * 100).toFixed(2)),
			roi: parseFloat((row.roi * 100).toFixed(2)),
			profit: row.profit,
			pvpNoIva: row.pvpdisbytenoiva || 0,
			meliFeeNoIva: row.melifeeadjnoiva || 0,
			freeShippingCost: isOpCostRow ? 0 : (row.freeshippingcostnoivar || 0),
			fixRate: isOpCostRow ? (row.freeshippingcostnoivar || 0) : (row.fixratenoiva || 0),
			pvpbuyer: row.pvpbuyer || 0,
			hasFreeShipping: !!row.freeshipping,
		} : null;

		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) =>
				s.id === id ? { ...updated, results: initialResults } : s
			),
		}));
	},

	lsRecalculate: (id) => {
		const scenario = get().lsScenarios.find((s) => s.id === id);
		if (!scenario || !scenario.pvpdisbyte || scenario.pvpdisbyte === 0) {
			set((state) => ({
				lsScenarios: state.lsScenarios.map((s) =>
					s.id === id ? { ...s, results: null } : s
				),
			}));
			return;
		}
		const results = calculateSimulatorScenario(scenario);
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) =>
				s.id === id ? { ...s, results } : s
			),
		}));
	},

	lsResetScenario: (id) => {
		const scenario = get().lsScenarios.find((s) => s.id === id);
		const currentDolar = scenario?.dolar;
		set((state) => ({
			lsScenarios: state.lsScenarios.map((s) => {
				if (s.id !== id) return s;
				const reset = buildDefaultScenario(id, scenario?.pais || 'ARGENTINA');
				return currentDolar ? { ...reset, dolar: currentDolar } : reset;
			}),
		}));
	},
});
